import { useEffect, useRef, useState } from 'react';
import type { UserRuleMastery } from '@proref/types';
import { usePrefersReducedMotion } from './AnimatedNumber';

// A dependency-free radar (spider) chart of per-rule accuracy. One axis per rule, the spoke length
// scaled by accuracy_score (0..1); the filled polygon is the referee's mastery shape. Drawn as
// inline SVG rather than pulling in Recharts for a single small chart (mirrors the leaderboard's
// inline Flame). Needs ≥3 axes to form a polygon — the caller gates on that.

const TWEEN_MS = 700; // matches AnimatedNumber's default, so the radar and the drill-list % agree
const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

// Tweens each axis's accuracy score toward its new value instead of snapping, so answering a clip
// visibly reshapes the polygon rather than jump-cutting to the new mastery. Keyed to the axis
// IDENTITY (rule ids, in order): if the axis SET itself changes (a different rule enters the
// top-sampled set), vertex index `i` no longer means the same rule between frames, so that's a
// structural change to snap to, not a value change to tween through.
function useAnimatedScores(rules: UserRuleMastery[]): number[] {
  const reduced = usePrefersReducedMotion();
  const scores = rules.map((r) => Math.max(0, Math.min(1, r.accuracy_score)));
  const axisKey = rules.map((r) => r.rule_article_id).join('|');

  const [display, setDisplay] = useState(scores);
  const displayRef = useRef(display);
  displayRef.current = display;
  const prevAxisKeyRef = useRef(axisKey);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const axesChanged = prevAxisKeyRef.current !== axisKey;
    prevAxisKeyRef.current = axisKey;

    if (reduced || axesChanged) {
      setDisplay(scores);
      return;
    }
    const start = displayRef.current;
    if (start.length === scores.length && start.every((v, i) => v === scores[i])) return;

    const startedAt = performance.now();
    const tick = (now: number): void => {
      const t = Math.min(1, (now - startedAt) / TWEEN_MS);
      const eased = easeOutCubic(t);
      setDisplay(start.map((s, i) => s + ((scores[i] ?? s) - s) * eased));
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
    // `scores` is a fresh array every render; axisKey + the joined values (dep below) are the real
    // change signal, and `display` is read via the ref so a running tween doesn't restart itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axisKey, reduced, scores.join(',')]);

  return display;
}

// Geometry in a square viewBox. A little padding leaves room for the numeric axis labels.
const SIZE = 220;
const CENTER = SIZE / 2;
const RADIUS = 78; // outer ring radius; labels sit just beyond it
const RINGS = [0.25, 0.5, 0.75, 1]; // concentric grid guides

// Point on an axis: axis `i` of `n`, at fractional radius `r` (0..1). First axis points straight up
// (−90°); the rest step clockwise so the shape reads like the design.
function point(i: number, n: number, r: number): { x: number; y: number } {
  const angle = -Math.PI / 2 + (i * 2 * Math.PI) / n;
  return {
    x: CENTER + Math.cos(angle) * RADIUS * r,
    y: CENTER + Math.sin(angle) * RADIUS * r,
  };
}

function polygon(points: Array<{ x: number; y: number }>): string {
  return points.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
}

interface MasteryRadarProps {
  rules: UserRuleMastery[];
  // An optional earlier shape drawn behind the current one, for a then-vs-now comparison. It MUST
  // share `rules`' axes, in the same order — the caller aligns them (the API does this server-side
  // for the admin profile), because two polygons over different axis sets cannot be overlaid.
  compare?: UserRuleMastery[];
}

export function MasteryRadar({ rules, compare }: MasteryRadarProps) {
  const n = rules.length;
  const animatedScores = useAnimatedScores(rules);
  // Grid rings (as regular polygons so they align with the axes) + spokes.
  const rings = RINGS.map((r) => polygon(Array.from({ length: n }, (_, i) => point(i, n, r))));
  // The mastery vertices, kept as points so we can both trace the polygon and dot each corner.
  // Radius comes from the tweened score, not `rule.accuracy_score` directly, so the shape eases
  // into a new mastery reading instead of snapping the moment the query refetches.
  const vertices = animatedScores.map((score, i) => point(i, n, score));
  const data = polygon(vertices);
  // Guard the length as well as the presence: a mismatched compare set would silently draw a
  // polygon whose vertices belong to the wrong axes, which reads as real movement.
  const past =
    compare && compare.length === n
      ? polygon(compare.map((rule, i) => point(i, n, Math.max(0, Math.min(1, rule.accuracy_score)))))
      : null;

  return (
    <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="h-full w-full" role="img" aria-label="Rule mastery radar">
      {/* Grid rings — lightest at the centre, drawn back-to-front. */}
      {rings.map((pts, i) => (
        <polygon key={i} points={pts} className="fill-none stroke-line-2" strokeWidth={1} />
      ))}
      {/* Axis spokes + the rule-number label at each vertex. */}
      {rules.map((rule, i) => {
        const outer = point(i, n, 1);
        const label = point(i, n, 1.16);
        return (
          <g key={rule.rule_article_id}>
            <line
              x1={CENTER}
              y1={CENTER}
              x2={outer.x}
              y2={outer.y}
              className="stroke-line-2"
              strokeWidth={1}
            />
            <text
              x={label.x}
              y={label.y}
              className="fill-muted text-[9px]"
              textAnchor="middle"
              dominantBaseline="middle"
            >
              {/* Just the leading segment ('8' of '8:5') keeps the axis labels legible. */}
              {rule.article_number.split(':')[0]}
            </text>
          </g>
        );
      })}
      {/* The earlier shape, drawn first so the current one sits on top of it. Dashed and unfilled:
          it is a reference outline, and filling it would muddy the overlap into a third colour that
          means nothing. */}
      {past && (
        <polygon
          points={past}
          className="fill-none stroke-muted-2"
          strokeWidth={1.5}
          strokeDasharray="4 3"
          strokeLinejoin="round"
        />
      )}
      {/* The mastery shape. fillOpacity is set explicitly (not via a `/25` class) because the
          accent token is a raw oklch() string, so Tailwind's alpha modifier can't tint it —
          without this the polygon renders as a solid opaque blob instead of a translucent shape. */}
      <polygon points={data} className="fill-accent stroke-accent" fillOpacity={0.18} strokeWidth={2} strokeLinejoin="round" />
      {/* A dot on each vertex so individual rule scores read as distinct points, matching the design. */}
      {vertices.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={2.6} className="fill-accent" />
      ))}
    </svg>
  );
}
